// hooks/useAnalytics.js
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import analyticsService from '../services/analyticsService'

export function usePageTracking () {
  const location = useLocation()
  const pageStartTime = useRef(Date.now())

  useEffect(() => {
    const pageName = location.pathname.replace('/', '') || 'home'

    // Track page view
    analyticsService.trackPageView(pageName, {
      pathname: location.pathname,
      search: location.search
    })

    pageStartTime.current = Date.now()

    // Cleanup: Track time on page
    return () => {
      const duration = Date.now() - pageStartTime.current
      analyticsService.trackTimeOnPage(pageName, duration)

      // Track bounce if user left quickly
      if (duration < 5000) {
        analyticsService.trackBounce(pageName, duration)
      }
    }
  }, [location])
}

export function usePerformanceMonitoring (componentName) {
  const renderStartTime = useRef(Date.now())
  const renderCount = useRef(0)

  useEffect(() => {
    renderCount.current++
    const renderTime = Date.now() - renderStartTime.current

    // Warn bei langsamen Renders
    if (renderTime > 100) {
      analyticsService.trackError('slow_render', {
        component: componentName,
        render_time: renderTime,
        render_count: renderCount.current,
        severity: 'warning'
      })
    }

    // Track excessive re-renders
    if (renderCount.current > 10) {
      analyticsService.trackError('excessive_rerenders', {
        component: componentName,
        render_count: renderCount.current,
        severity: 'warning'
      })
    }

    renderStartTime.current = Date.now()
  }, [componentName])
}

export function useErrorTracking () {
  useEffect(() => {
    const handleError = event => {
      analyticsService.trackError('unhandled_error', {
        message: event.error?.message || 'Unknown error',
        stack: event.error?.stack,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        severity: 'high'
      })
    }

    const handleRejection = event => {
      analyticsService.trackError('unhandled_rejection', {
        reason: event.reason?.toString() || 'Unknown rejection',
        severity: 'high'
      })
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleRejection)

    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleRejection)
    }
  }, [])
}

export function useFeatureTracking (featureName) {
  const hasTracked = useRef(false)

  useEffect(() => {
    if (!hasTracked.current) {
      analyticsService.trackFeatureUsage(featureName, {
        timestamp: new Date().toISOString()
      })
      hasTracked.current = true
    }
  }, [featureName])
}

export function useEngagementTracking () {
  const interactionCount = useRef(0)
  const sessionStartTime = useRef(Date.now())

  useEffect(() => {
    const trackInteraction = () => {
      interactionCount.current++
    }

    const events = ['click', 'scroll', 'keydown', 'mousemove']
    events.forEach(event => {
      document.addEventListener(event, trackInteraction, { passive: true })
    })

    // Track engagement every 30 seconds
    const interval = setInterval(() => {
      const duration = Date.now() - sessionStartTime.current

      analyticsService.trackEngagement('periodic_check', {
        duration: duration / 1000,
        interactionCount: interactionCount.current
      })
    }, 30000)

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, trackInteraction)
      })
      clearInterval(interval)

      // Final engagement report
      const totalDuration = Date.now() - sessionStartTime.current
      analyticsService.trackEngagement('session_summary', {
        duration: totalDuration / 1000,
        interactionCount: interactionCount.current
      })
    }
  }, [])
}

// Network Performance Monitoring
export function useNetworkMonitoring () {
  useEffect(() => {
    if (!('PerformanceObserver' in window)) return

    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'resource') {
          // Track slow network requests
          if (entry.duration > 2000) {
            analyticsService.trackError('slow_network_request', {
              url: entry.name,
              duration: entry.duration,
              size: entry.transferSize,
              severity: 'warning'
            })
          }
        }
      }
    })

    observer.observe({ entryTypes: ['resource', 'navigation'] })

    return () => observer.disconnect()
  }, [])
}

export default {
  usePageTracking,
  usePerformanceMonitoring,
  useErrorTracking,
  useFeatureTracking,
  useEngagementTracking,
  useNetworkMonitoring
}
